export type PriceCategory = "low-budget" | "mid-budget" | "high-budget" | "luxury";
export type ProductType = "shoes" | "clothing" | "outdoor" | "sport" | "accessories";

export interface SubScores {
  [key: string]: number | undefined;
}

export interface Brand {
  id: string;
  name: string;
  logo: string;
  category: string;
  country: string;
  website: string;
  headquarter?: string;
  companySize?: string;
  geoProfile?: string;
  productSegment?: string;
  fashionType?: string;
  targetCustomer?: string[];
  productionCountries?: string[];
  veganBrand?: string;
  plusSize?: string;
  tags?: string[];
  priceCategory: PriceCategory;
  productTypes: string[];
  scores: {
    human: number;
    manufacturing: number;
    product: number;
    logistics: number;
    citizenship: number;
  };
  subScores?: {
    human?: SubScores;
    manufacturing?: SubScores;
    product?: SubScores;
    logistics?: SubScores;
    citizenship?: SubScores;
  };
  subScoreElements?: Record<string, string>;
  subScoreElementsFr?: Record<string, string>;
  overallScore: number;
  overallSummary?: string;
  overallSummaryFr?: string;
  pillarSummaries?: {
    human?: string;
    humanFr?: string;
    manufacturing?: string;
    manufacturingFr?: string;
    product?: string;
    productFr?: string;
    logistics?: string;
    logisticsFr?: string;
    citizenship?: string;
    citizenshipFr?: string;
  };
  fullReview?: string;
  fullReviewFr?: string;
  description: string;
  descriptionEn: string;
}

export const brands: Brand[] = [
  {
    id: "patagonia",
    name: "Patagonia",
    logo: "https://upload.wikimedia.org/wikipedia/en/thumb/b/bd/Patagonia_%28clothing%29_logo.svg/320px-Patagonia_%28clothing%29_logo.svg.png",
    category: "Outdoor",
    country: "USA",
    website: "https://www.patagonia.com",
    priceCategory: "high-budget",
    productTypes: ["outdoor", "clothing"],
    scores: { human: 92, manufacturing: 95, product: 90, logistics: 70, citizenship: 88 },
    overallScore: 87,
    description: "Pionnier de la mode durable avec un engagement fort pour l'environnement.",
    descriptionEn: "Pioneer of sustainable fashion with a strong environmental commitment."
  },
  {
    id: "veja",
    name: "Veja",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/Veja_%28brand%29_logo.svg/320px-Veja_%28brand%29_logo.svg.png",
    category: "Chaussures",
    country: "France",
    website: "https://www.veja-store.com",
    priceCategory: "mid-budget",
    productTypes: ["shoes"],
    scores: { human: 88, manufacturing: 90, product: 85, logistics: 92, citizenship: 95 },
    overallScore: 90,
    description: "Sneakers éco-responsables fabriquées au Brésil avec transparence totale.",
    descriptionEn: "Eco-friendly sneakers made in Brazil with full transparency."
  },
  {
    id: "arket",
    name: "Arket",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/89/Arket_logo.svg/320px-Arket_logo.svg.png",
    category: "Prêt-à-porter",
    country: "Suède",
    website: "https://www.arket.com",
    priceCategory: "mid-budget",
    productTypes: ["clothing", "accessories"],
    scores: { human: 75, manufacturing: 78, product: 82, logistics: 80, citizenship: 70 },
    overallScore: 77,
    description: "Marque H&M Group avec une approche plus durable et transparente.",
    descriptionEn: "H&M Group brand with a more sustainable and transparent approach."
  },
  {
    id: "zara",
    name: "Zara",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fd/Zara_Logo.svg/320px-Zara_Logo.svg.png",
    category: "Fast Fashion",
    country: "Espagne",
    website: "https://www.zara.com",
    priceCategory: "mid-budget",
    productTypes: ["clothing", "shoes", "accessories"],
    scores: { human: 45, manufacturing: 40, product: 35, logistics: 60, citizenship: 55 },
    overallScore: 47,
    description: "Leader de la fast fashion avec des efforts récents en durabilité.",
    descriptionEn: "Fast fashion leader with recent sustainability efforts."
  },
  {
    id: "sezane",
    name: "Sézane",
    logo: "https://upload.wikimedia.org/wikipedia/fr/thumb/4/4b/S%C3%A9zane_logo.svg/320px-S%C3%A9zane_logo.svg.png",
    category: "Prêt-à-porter",
    country: "France",
    website: "https://www.sezane.com",
    priceCategory: "high-budget",
    productTypes: ["clothing", "accessories", "shoes"],
    scores: { human: 82, manufacturing: 80, product: 88, logistics: 90, citizenship: 92 },
    overallScore: 86,
    description: "Mode parisienne responsable avec programme de seconde main.",
    descriptionEn: "Responsible Parisian fashion with second-hand program."
  },
  {
    id: "hm",
    name: "H&M",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/H%26M-Logo.svg/320px-H%26M-Logo.svg.png",
    category: "Fast Fashion",
    country: "Suède",
    website: "https://www.hm.com",
    priceCategory: "low-budget",
    productTypes: ["clothing", "accessories"],
    scores: { human: 50, manufacturing: 48, product: 40, logistics: 55, citizenship: 60 },
    overallScore: 51,
    description: "Géant suédois avec programme Conscious mais modèle fast fashion.",
    descriptionEn: "Swedish giant with Conscious program but fast fashion model."
  },
  {
    id: "nike",
    name: "Nike",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a6/Logo_NIKE.svg/320px-Logo_NIKE.svg.png",
    category: "Sport",
    country: "USA",
    website: "https://www.nike.com",
    priceCategory: "mid-budget",
    productTypes: ["sport", "shoes", "clothing"],
    scores: { human: 55, manufacturing: 60, product: 75, logistics: 45, citizenship: 50 },
    overallScore: 57,
    description: "Leader sportif avec efforts de durabilité mais production offshore.",
    descriptionEn: "Sports leader with sustainability efforts but offshore production."
  },
  {
    id: "ecoalf",
    name: "Ecoalf",
    logo: "https://www.ecoalf.com/cdn/shop/files/ECOALF_LOGO_HORIZONTAL.svg?v=1706094405&width=150",
    category: "Outdoor",
    country: "Espagne",
    website: "https://www.ecoalf.com",
    priceCategory: "high-budget",
    productTypes: ["outdoor", "clothing", "accessories"],
    scores: { human: 85, manufacturing: 92, product: 88, logistics: 85, citizenship: 88 },
    overallScore: 88,
    description: "Première marque B Corp espagnole, utilise plastique océanique recyclé.",
    descriptionEn: "First Spanish B Corp brand, uses recycled ocean plastic."
  },
  {
    id: "shein",
    name: "Shein",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Shein_logo.svg/320px-Shein_logo.svg.png",
    category: "Ultra Fast Fashion",
    country: "Chine",
    website: "https://www.shein.com",
    priceCategory: "low-budget",
    productTypes: ["clothing", "accessories", "shoes"],
    scores: { human: 20, manufacturing: 15, product: 15, logistics: 10, citizenship: 5 },
    overallScore: 13,
    description: "Ultra fast fashion avec très peu de transparence sur la chaîne.",
    descriptionEn: "Ultra fast fashion with very little supply chain transparency."
  },
  {
    id: "faguo",
    name: "Faguo",
    logo: "https://cdn.shopify.com/s/files/1/0012/8561/1409/files/logo_faguo.svg",
    category: "Prêt-à-porter",
    country: "France",
    website: "https://www.faguo.com",
    priceCategory: "mid-budget",
    productTypes: ["clothing", "shoes", "accessories"],
    scores: { human: 80, manufacturing: 82, product: 78, logistics: 88, citizenship: 90 },
    overallScore: 84,
    description: "Marque française plantant un arbre pour chaque produit vendu.",
    descriptionEn: "French brand planting a tree for every product sold."
  },
  {
    id: "boohoo",
    name: "Boohoo",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Boohoo_logo.svg/320px-Boohoo_logo.svg.png",
    category: "Ultra Fast Fashion",
    country: "UK",
    website: "https://www.boohoo.com",
    priceCategory: "low-budget",
    productTypes: ["clothing", "accessories"],
    scores: { human: 25, manufacturing: 30, product: 25, logistics: 35, citizenship: 40 },
    overallScore: 31,
    description: "Fast fashion britannique avec controverses sur conditions de travail.",
    descriptionEn: "British fast fashion with controversies over working conditions."
  },
  {
    id: "asphalte",
    name: "Asphalte",
    logo: "https://www.asphalte.com/static/logo-edd4b0d98c.svg",
    category: "Prêt-à-porter",
    country: "France",
    website: "https://www.asphalte.com",
    priceCategory: "mid-budget",
    productTypes: ["clothing"],
    scores: { human: 85, manufacturing: 88, product: 90, logistics: 92, citizenship: 94 },
    overallScore: 90,
    description: "Précommande uniquement, production européenne, zéro surstock.",
    descriptionEn: "Pre-order only, European production, zero overstock."
  },
];

export type QuadrantCategory = "eclaireurs" | "pionniers" | "proximite" | "retardataires";

export const getQuadrantCategory = (brand: Brand): QuadrantCategory => {
  const productIntegrity = (brand.scores.human + brand.scores.manufacturing + brand.scores.product) / 3;
  const territorialCoherence = (brand.scores.logistics + brand.scores.citizenship) / 2;
  
  if (productIntegrity >= 50 && territorialCoherence >= 50) return "eclaireurs";
  if (productIntegrity >= 50 && territorialCoherence < 50) return "pionniers";
  if (productIntegrity < 50 && territorialCoherence >= 50) return "proximite";
  return "retardataires";
};

export const getPillarInfo = (pillar: keyof Brand['scores']) => {
  const pillars: Record<keyof Brand['scores'], { name: string; icon: string; iconName: string; description: string; color: string }> = {
    human: {
      name: "Éthique",
      icon: "👥",
      iconName: "Users",
      description: "Droit du travail · Traçabilité · Labels sociaux",
      color: "pillar-human"
    },
    manufacturing: {
      name: "Écologie",
      icon: "🌿",
      iconName: "Leaf",
      description: "Matières · Santé · Climat",
      color: "pillar-manufacturing"
    },
    product: {
      name: "Durabilité",
      icon: "🛡️",
      iconName: "ShieldCheck",
      description: "Surproduction · Qualité · Réparation",
      color: "pillar-product"
    },
    logistics: {
      name: "Proximité",
      icon: "📍",
      iconName: "MapPin",
      description: "Trajet · Zéro Avion · Économie locale",
      color: "pillar-logistics"
    },
    citizenship: {
      name: "Transparence",
      icon: "👁️",
      iconName: "Eye",
      description: "Fiscalité · Propriété · Anti-Bullshit",
      color: "pillar-citizenship"
    }
  };
  return pillars[pillar];
};
